import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { supabase } from '../supabaseClient';

const ChatwootConfigDialog = ({ open, onClose }) => {
  const [config, setConfig] = useState({
    chatwoot_account_id: '',
    webhook_url_token: '',
    chatwoot_agent_bot_access_token: '',
  });
  const [saving, setSaving] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchExistingConfig = async () => {
      setLoadingConfig(true);
      setError('');
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw new Error(`Supabase auth error: ${sessionError.message}`);
        if (!session?.user?.id) throw new Error('No active user session found. Please log in to load configuration.');
        const userId = session.user.id;
        const { data: existingConfig, error: fetchError } = await supabase
          .from('chatwoot_configurations')
          .select('chatwoot_account_id, webhook_url_token, chatwoot_agent_bot_access_token')
          .eq('rag_user_id', userId)
          .maybeSingle();
        if (fetchError) throw new Error(fetchError.message);
        if (existingConfig) {
          setConfig({
            chatwoot_account_id: existingConfig.chatwoot_account_id?.toString() || '',
            webhook_url_token: existingConfig.webhook_url_token || '',
            chatwoot_agent_bot_access_token: existingConfig.chatwoot_agent_bot_access_token || '',
          });
        }
      } catch (e) {
        setError(e.message || 'Failed to load configuration.');
      } finally {
        setLoadingConfig(false);
      }
    };
    if (open) {
      setError('');
      setSuccess('');
      fetchExistingConfig();
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setConfig(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw new Error(`Supabase auth error: ${sessionError.message}`);
      if (!session?.access_token) throw new Error('No active user session found. Please log in.');

      // Validate required fields
      if (!config.chatwoot_account_id || isNaN(parseInt(config.chatwoot_account_id, 10))) {
        setError('Chatwoot Account ID must be a valid number.');
        setSaving(false);
        return;
      }
      if (!config.webhook_url_token.trim()) {
        setError('Webhook URL Token cannot be empty.');
        setSaving(false);
        return;
      }
      if (!config.chatwoot_agent_bot_access_token.trim()) {
        setError('Agent Bot Access Token cannot be empty.');
        setSaving(false);
        return;
      }

      const payload = {
        chatwoot_account_id: parseInt(config.chatwoot_account_id, 10),
        webhook_url_token: config.webhook_url_token.trim(),
        plain_agent_bot_token: config.chatwoot_agent_bot_access_token.trim(),
        is_active: true,
      };

      const resp = await fetch('/chatwoot/configure', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({ detail: resp.statusText }));
        throw new Error(errData.detail || resp.statusText);
      }
      setSuccess('Chatwoot configuration saved successfully!');
      setTimeout(() => onClose(), 1200);
    } catch (e) {
      setError(e.message || 'Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="chatwoot-config-dialog-title">
      <DialogTitle id="chatwoot-config-dialog-title">Connect Your Chatwoot (Paid Customers)</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Enter the minimum details to let our AI receive webhooks from your Chatwoot Agent Bot and post replies back.
        </DialogContentText>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
          <TextField
            autoFocus
            required
            margin="dense"
            id="chatwoot_account_id"
            name="chatwoot_account_id"
            label="Chatwoot Account ID"
            type="number"
            fullWidth
            variant="outlined"
            value={config.chatwoot_account_id}
            onChange={handleChange}
            helperText="Numeric ID of your Chatwoot account. Find it in the URL (/accounts/{id}) or in Account settings."
            disabled={saving || loadingConfig}
          />

          <TextField
            required
            margin="dense"
            id="webhook_url_token"
            name="webhook_url_token"
            label="Webhook URL Token (Secret)"
            type="text"
            fullWidth
            variant="outlined"
            value={config.webhook_url_token}
            onChange={handleChange}
            helperText="Create a random secret and append it to your Bot Outgoing URL: https://your-backend/chatwoot/webhook?token=YOUR_SECRET. We verify this on every webhook."
            disabled={saving || loadingConfig}
          />

          <TextField
            required
            margin="dense"
            id="chatwoot_agent_bot_access_token"
            name="chatwoot_agent_bot_access_token"
            label="Agent Bot Access Token"
            type="password"
            fullWidth
            variant="outlined"
            value={config.chatwoot_agent_bot_access_token}
            onChange={handleChange}
            helperText="Bot-scoped token from Chatwoot (Settings → Bots). Do not use admin tokens. We store it encrypted."
            disabled={saving || loadingConfig}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ pb: 2, pr: 2 }}>
        <Button onClick={onClose} disabled={saving || loadingConfig}>Cancel</Button>
        <Box sx={{ position: 'relative' }}>
          <Button variant="contained" onClick={handleSave} disabled={saving || loadingConfig}>Save</Button>
          {saving && (
            <CircularProgress size={24} sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-12px', marginLeft: '-12px' }} />
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ChatwootConfigDialog;